/**
 * One row of the listings table.
 *
 * Reads only what the admin list response already carries plus the units and
 * cities the page already loads. It writes nothing: both status actions go out
 * through the same `onPatchStatus` the table had before, with the same bodies.
 */
import React, { useState } from "react";
import { Pencil } from "lucide-react";

import TenancyRowActions from "../tenants/TenancyRowActions";
import { ListingAvailabilityBadge, ListingPublishBadge } from "./ListingStatusBadge";
import { describeListingObject } from "./utils/listingHelpers";
import { cityLabel, formatChf, primaryImageUrl } from "./utils/listingFormatting";

const AVAILABILITY_ACTIONS = [
  { value: "available", label: "Als verfügbar markieren" },
  { value: "occupied", label: "Als belegt markieren" },
  { value: "unavailable", label: "Als nicht verfügbar markieren" },
];

/** Neutral stand-in when a listing has no image, or its URL failed to load. */
function ThumbnailPlaceholder() {
  return (
    <div
      aria-hidden="true"
      className="flex h-11 w-16 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-[10px] font-semibold text-muted-foreground"
    >
      Kein Bild
    </div>
  );
}

export default function ListingRow({
  listing,
  units,
  cities,
  isSelected,
  isUpdating,
  onEdit,
  onPatchStatus,
}) {
  const [imageFailed, setImageFailed] = useState(false);

  const { shortId, roomLabel, typeLabel } = describeListingObject(listing, units);
  const objectLabel = `${shortId} · ${roomLabel}`;
  const title = listing.title_de || listing.title_en || listing.id;
  const imageUrl = imageFailed ? null : primaryImageUrl(listing);
  const status = listing.availability_status || "available";

  const actions = [
    {
      key: "publish",
      label: listing.is_published ? "Ausblenden" : "Veröffentlichen",
      onSelect: () => onPatchStatus(listing.id, { is_published: !listing.is_published }),
    },
    ...AVAILABILITY_ACTIONS.filter((a) => a.value !== status).map((a) => ({
      key: `availability-${a.value}`,
      label: a.label,
      onSelect: () => onPatchStatus(listing.id, { availability_status: a.value }),
    })),
  ];

  return (
    <tr
      /* Names WHICH of the listed rows the editor below is showing. */
      aria-current={isSelected ? "true" : undefined}
      data-selected={isSelected ? "true" : undefined}
      className={`border-b border-border last:border-0 ${
        isSelected ? "bg-primary/5" : "hover:bg-muted/40"
      } ${isUpdating ? "opacity-60" : ""}`}
    >
      {/* The accent bar is always present and only changes colour, so
          selecting a row never shifts the column by 2px. */}
      <td
        className={`border-l-2 px-4 py-3 ${
          isSelected ? "border-l-primary" : "border-l-transparent"
        }`}
      >
        <div className="flex items-center gap-3">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={`Vorschaubild ${objectLabel}`}
              loading="lazy"
              width={64}
              height={44}
              onError={() => setImageFailed(true)}
              className="h-11 w-16 shrink-0 rounded-md border border-border object-cover"
            />
          ) : (
            <ThumbnailPlaceholder />
          )}
          <div className="min-w-0">
            <div className="truncate text-[13px] font-semibold text-foreground">
              {objectLabel}
            </div>
            <div className="mt-0.5 truncate text-[11px] text-muted-foreground">
              {typeLabel}
            </div>
          </div>
        </div>
      </td>

      <td className="px-4 py-3 text-[13px] text-foreground">
        {/* 220px, not more: the table's floor is the sum of its columns, and
            at 1440px — sidebar and padding removed — the card gives it 1070.
            At 320 the floor was 1161, so the 91px that did not fit were the
            actions column: "Bearbeiten" and the kebab sat past the edge of a
            wrapper whose scrollbar is invisible on most systems. Capping the
            title here brings the floor to exactly 1070. Narrower viewports
            still scroll the wrapper, which is what it is for. */}
        <div className="max-w-[220px] truncate" title={title}>
          {title}
        </div>
      </td>

      <td className="whitespace-nowrap px-4 py-3 text-[13px] text-foreground">
        {cityLabel(listing, cities)}
      </td>

      <td className="whitespace-nowrap px-4 py-3 text-[13px] font-semibold text-foreground">
        {formatChf(listing.price_chf_month)}
      </td>

      <td className="px-4 py-3">
        <ListingPublishBadge isPublished={listing.is_published} />
      </td>

      <td className="px-4 py-3">
        <ListingAvailabilityBadge status={status} />
      </td>

      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => onEdit(listing)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Pencil aria-hidden="true" className="h-3.5 w-3.5" />
            Bearbeiten
          </button>
          <TenancyRowActions items={actions} label={`Aktionen für ${objectLabel}`} />
        </div>
      </td>
    </tr>
  );
}
